// ==============================================
// Health Module Card
// ==============================================
//
// Generic expandable card used to host a single health module (e.g. Kiosk
// Health) on the device Health tab. Collapsed header shows title, status
// badge(s), and an optional subtitle. Expanded body shows whatever the caller
// passes as children.
//
// The card is purely presentational — health computation, refetching, and
// data fetching all stay in the module that uses it. Adding a new health
// module (GPS / Battery / Network / Storage) means dropping another instance
// of this card onto the Health tab with its own children.

import { useState, type ReactNode, type KeyboardEvent } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

export type HealthModuleTone = 'ok' | 'warn' | 'fail' | 'neutral';

const TONE_PILL: Record<HealthModuleTone, { pill: string; dot: string }> = {
  ok: { pill: 'border-green-700/50 bg-green-900/30 text-green-300', dot: 'bg-green-400' },
  warn: { pill: 'border-amber-700/50 bg-amber-900/30 text-amber-300', dot: 'bg-amber-400' },
  fail: { pill: 'border-red-700/50 bg-red-900/30 text-red-300', dot: 'bg-red-400' },
  neutral: { pill: 'border-slate-700 bg-slate-800/50 text-slate-300', dot: 'bg-slate-400' },
};

export interface HealthModuleBadge {
  tone: HealthModuleTone;
  text: string;
}

export interface HealthModuleCardProps {
  title: string;
  status: HealthModuleTone;
  statusText: string;
  statusBadges?: HealthModuleBadge[];
  subtitle?: ReactNode;
  headerActions?: ReactNode;
  defaultExpanded?: boolean;
  children?: ReactNode;
}

function Badge({ tone, text, size = 'lg' }: HealthModuleBadge & { size?: 'lg' | 'sm' }) {
  const cls = TONE_PILL[tone];
  const sizing =
    size === 'lg' ? 'px-3 py-1 text-sm gap-2' : 'px-2 py-0.5 text-[10px] gap-1.5';
  const dotSize = size === 'lg' ? 'h-2 w-2' : 'h-1 w-1';
  return (
    <span
      className={`inline-flex items-center rounded-full border font-medium ${cls.pill} ${sizing}`}
    >
      <span className={`rounded-full ${cls.dot} ${dotSize}`} />
      {text}
    </span>
  );
}

export default function HealthModuleCard({
  title,
  status,
  statusText,
  statusBadges,
  subtitle,
  headerActions,
  defaultExpanded = false,
  children,
}: HealthModuleCardProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const toggle = () => setExpanded((v) => !v);
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle();
    }
  };

  return (
    <div className="rounded-lg border border-slate-700 bg-slate-800/50">
      <div
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={toggle}
        onKeyDown={onKeyDown}
        className="flex w-full cursor-pointer items-start justify-between gap-4 px-5 py-4 hover:bg-slate-700/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/40 rounded-lg"
      >
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center text-slate-400">
            {expanded ? (
              <ChevronDownIcon className="h-4 w-4" />
            ) : (
              <ChevronRightIcon className="h-4 w-4" />
            )}
          </span>
          <div className="min-w-0 flex-1 space-y-1.5">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider">
                {title}
              </h3>
              <Badge tone={status} text={statusText} />
              {statusBadges?.map((b, i) => (
                <Badge key={`${b.text}-${i}`} tone={b.tone} text={`also: ${b.text}`} size="sm" />
              ))}
            </div>
            {subtitle && <div className="text-xs text-slate-500">{subtitle}</div>}
          </div>
        </div>
        {headerActions && (
          <div
            className="flex flex-none items-center gap-2"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            {headerActions}
          </div>
        )}
      </div>
      {expanded && children && (
        <div className="border-t border-slate-700 p-5 space-y-5">{children}</div>
      )}
    </div>
  );
}
